import { openDB, type DBSchema, type IDBPDatabase } from "idb";

import { checksum } from "./checksum.js";
import { migrateSave, type SaveMigration } from "./migrations.js";
import {
  SaveImportError,
  SaveStorageError,
  type SaveRecoveryReport,
  type SaveRepository,
  type SaveRepositoryOptions,
  type SaveSlot,
} from "./repository.js";
import {
  CURRENT_SAVE_SCHEMA_VERSION,
  parseSaveEnvelope,
  readSaveSchemaVersion,
  type JsonValue,
  type SaveEnvelope,
} from "./schema.js";

const DATABASE_VERSION = 1;
const DEFAULT_DATABASE_NAME = "stormglass-save-v1";
const DEFAULT_MAX_IMPORT_BYTES = 2 * 1024 * 1024;
const METADATA_KEY = "state";

interface StoredSave {
  readonly slot: SaveSlot;
  readonly envelope: SaveEnvelope;
  readonly checksum: string;
  readonly writtenAt: string;
}

interface SaveMetadata {
  readonly key: typeof METADATA_KEY;
  readonly activeSlot: SaveSlot | null;
  readonly backupSlot: SaveSlot | null;
  readonly lastWriteAt: string | null;
}

interface SaveDatabase extends DBSchema {
  slots: {
    key: SaveSlot;
    value: StoredSave;
  };
  metadata: {
    key: string;
    value: SaveMetadata;
  };
}

function asJsonValue(value: SaveEnvelope): JsonValue {
  return value;
}

function describeError(error: unknown): string {
  return error instanceof Error
    ? `${error.name}: ${error.message}`
    : "Unknown persistence error";
}

function oppositeSlot(slot: SaveSlot | null): SaveSlot {
  return slot === "a" ? "b" : "a";
}

function emptyMetadata(): SaveMetadata {
  return {
    key: METADATA_KEY,
    activeSlot: null,
    backupSlot: null,
    lastWriteAt: null,
  };
}

export class IndexedDBSaveRepository implements SaveRepository {
  readonly #databaseName: string;
  readonly #maxImportBytes: number;
  readonly #migrations: readonly SaveMigration[];
  readonly #now: () => Date;
  #databasePromise: Promise<IDBPDatabase<SaveDatabase>> | null = null;
  #writeQueue: Promise<void> = Promise.resolve();
  #lastRecoveryReport: SaveRecoveryReport = {
    recoveredFromBackup: false,
    activeSlot: null,
    backupSlot: null,
    activeError: null,
    backupError: null,
  };

  public constructor(options: SaveRepositoryOptions = {}) {
    this.#databaseName = options.databaseName ?? DEFAULT_DATABASE_NAME;
    this.#maxImportBytes = options.maxImportBytes ?? DEFAULT_MAX_IMPORT_BYTES;
    this.#migrations = options.migrations ?? [];
    this.#now = options.now ?? (() => new Date());
  }

  public getLastRecoveryReport(): SaveRecoveryReport {
    return this.#lastRecoveryReport;
  }

  public async load(): Promise<SaveEnvelope | null> {
    await this.#writeQueue;
    try {
      const database = await this.#database();
      const transaction = database.transaction(
        ["metadata", "slots"],
        "readonly",
      );
      const metadata =
        (await transaction.objectStore("metadata").get(METADATA_KEY)) ??
        emptyMetadata();
      const activeStored =
        metadata.activeSlot === null
          ? undefined
          : await transaction.objectStore("slots").get(metadata.activeSlot);
      const backupStored =
        metadata.backupSlot === null
          ? undefined
          : await transaction.objectStore("slots").get(metadata.backupSlot);
      await transaction.done;

      let activeError: string | null = null;
      if (activeStored !== undefined) {
        try {
          const { migrated, save } = this.#readStored(activeStored);
          if (migrated) await this.save(save);
          this.#lastRecoveryReport = {
            recoveredFromBackup: false,
            activeSlot: migrated
              ? oppositeSlot(metadata.activeSlot)
              : metadata.activeSlot,
            backupSlot: migrated ? metadata.activeSlot : metadata.backupSlot,
            activeError: null,
            backupError: null,
          };
          return save;
        } catch (error) {
          activeError = describeError(error);
        }
      } else if (metadata.activeSlot !== null) {
        activeError = `Save slot ${metadata.activeSlot} is missing.`;
      }

      let backupError: string | null = null;
      if (backupStored !== undefined) {
        try {
          const { migrated, save } = this.#readStored(backupStored);
          if (migrated) await this.save(save);
          this.#lastRecoveryReport = {
            recoveredFromBackup: true,
            activeSlot: migrated
              ? oppositeSlot(metadata.activeSlot)
              : metadata.activeSlot,
            backupSlot: migrated ? metadata.activeSlot : metadata.backupSlot,
            activeError,
            backupError: null,
          };
          return save;
        } catch (error) {
          backupError = describeError(error);
        }
      } else if (metadata.backupSlot !== null) {
        backupError = `Backup slot ${metadata.backupSlot} is missing.`;
      }

      this.#lastRecoveryReport = {
        recoveredFromBackup: false,
        activeSlot: metadata.activeSlot,
        backupSlot: metadata.backupSlot,
        activeError,
        backupError,
      };
      if (activeError !== null || backupError !== null) {
        throw new Error("No valid local save or backup could be loaded.");
      }
      return null;
    } catch (error) {
      throw new SaveStorageError("Failed to load local progression.", "load", {
        cause: error,
      });
    }
  }

  public async save(data: SaveEnvelope): Promise<void> {
    const validated = parseSaveEnvelope(data);
    return this.#enqueueWrite(async () => {
      try {
        const database = await this.#database();
        const transaction = database.transaction(
          ["metadata", "slots"],
          "readwrite",
        );
        const metadata =
          (await transaction.objectStore("metadata").get(METADATA_KEY)) ??
          emptyMetadata();
        const nextSlot = oppositeSlot(metadata.activeSlot);
        const writtenAt = this.#now().toISOString();
        const stored: StoredSave = {
          slot: nextSlot,
          envelope: validated,
          checksum: checksum(asJsonValue(validated)),
          writtenAt,
        };
        await transaction.objectStore("slots").put(stored);
        await transaction.objectStore("metadata").put({
          key: METADATA_KEY,
          activeSlot: nextSlot,
          backupSlot: metadata.activeSlot,
          lastWriteAt: writtenAt,
        });
        await transaction.done;
      } catch (error) {
        throw new SaveStorageError(
          "Failed to save local progression.",
          "save",
          { cause: error },
        );
      }
    });
  }

  public async loadBackup(): Promise<SaveEnvelope | null> {
    await this.#writeQueue;
    try {
      const database = await this.#database();
      const transaction = database.transaction(
        ["metadata", "slots"],
        "readonly",
      );
      const metadata =
        (await transaction.objectStore("metadata").get(METADATA_KEY)) ??
        emptyMetadata();
      const stored =
        metadata.backupSlot === null
          ? undefined
          : await transaction.objectStore("slots").get(metadata.backupSlot);
      await transaction.done;
      return stored === undefined ? null : this.#readStored(stored).save;
    } catch (error) {
      throw new SaveStorageError("Failed to load the local backup.", "load", {
        cause: error,
      });
    }
  }

  public async clear(): Promise<void> {
    return this.#enqueueWrite(async () => {
      try {
        const database = await this.#database();
        const transaction = database.transaction(
          ["metadata", "slots"],
          "readwrite",
        );
        await transaction.objectStore("slots").clear();
        await transaction.objectStore("metadata").put(emptyMetadata());
        await transaction.done;
        this.#lastRecoveryReport = {
          recoveredFromBackup: false,
          activeSlot: null,
          backupSlot: null,
          activeError: null,
          backupError: null,
        };
      } catch (error) {
        throw new SaveStorageError(
          "Failed to clear local progression.",
          "clear",
          { cause: error },
        );
      }
    });
  }

  public async export(): Promise<Blob> {
    const save = await this.load();
    if (save === null) {
      throw new SaveStorageError("There is no local save to export.", "export");
    }
    return new Blob([JSON.stringify(save, null, 2)], {
      type: "application/json",
    });
  }

  public async import(blob: Blob): Promise<SaveEnvelope> {
    if (blob.size > this.#maxImportBytes) {
      throw new SaveImportError(
        `Save import exceeds the ${String(this.#maxImportBytes)}-byte limit.`,
      );
    }
    let decoded: unknown;
    try {
      decoded = JSON.parse(await blob.text()) as unknown;
    } catch (error) {
      throw new SaveImportError("Save import is not valid JSON.", {
        cause: error,
      });
    }

    let migrated: SaveEnvelope;
    try {
      migrated = migrateSave(decoded, this.#migrations);
    } catch (error) {
      throw new SaveImportError("Save import is invalid or incompatible.", {
        cause: error,
      });
    }
    await this.save(migrated);
    return migrated;
  }

  public async exportDiagnostics(): Promise<Blob> {
    await this.#writeQueue;
    const database = await this.#database();
    const transaction = database.transaction(["metadata", "slots"], "readonly");
    const metadata =
      (await transaction.objectStore("metadata").get(METADATA_KEY)) ??
      emptyMetadata();
    const slots = await transaction.objectStore("slots").getAll();
    await transaction.done;

    const diagnostics = {
      generatedAt: this.#now().toISOString(),
      databaseName: this.#databaseName,
      databaseVersion: DATABASE_VERSION,
      activeSlot: metadata.activeSlot,
      backupSlot: metadata.backupSlot,
      lastWriteAt: metadata.lastWriteAt,
      recovery: this.#lastRecoveryReport,
      slots: slots.map((stored) => ({
        slot: stored.slot,
        checksumValid:
          checksum(asJsonValue(stored.envelope)) === stored.checksum,
        writtenAt: stored.writtenAt,
        gameVersion: stored.envelope.gameVersion,
        contentVersion: stored.envelope.contentVersion,
        simulationVersion: stored.envelope.simulationVersion,
        saveSchemaVersion: stored.envelope.saveSchemaVersion,
        hasActiveRun: stored.envelope.activeRun !== null,
      })),
    };
    return new Blob([JSON.stringify(diagnostics, null, 2)], {
      type: "application/json",
    });
  }

  async #database(): Promise<IDBPDatabase<SaveDatabase>> {
    this.#databasePromise ??= openDB<SaveDatabase>(
      this.#databaseName,
      DATABASE_VERSION,
      {
        upgrade(database) {
          if (!database.objectStoreNames.contains("slots")) {
            database.createObjectStore("slots", { keyPath: "slot" });
          }
          if (!database.objectStoreNames.contains("metadata")) {
            database.createObjectStore("metadata", { keyPath: "key" });
          }
        },
      },
    );
    return this.#databasePromise;
  }

  #readStored(stored: StoredSave): {
    readonly save: SaveEnvelope;
    readonly migrated: boolean;
  } {
    if (checksum(asJsonValue(stored.envelope)) !== stored.checksum) {
      throw new Error(
        `Save slot ${stored.slot} failed its integrity checksum.`,
      );
    }
    const sourceVersion = readSaveSchemaVersion(stored.envelope);
    return {
      save: migrateSave(stored.envelope, this.#migrations),
      migrated: sourceVersion !== CURRENT_SAVE_SCHEMA_VERSION,
    };
  }

  #enqueueWrite(operation: () => Promise<void>): Promise<void> {
    const result = this.#writeQueue.then(operation, operation);
    this.#writeQueue = result.catch(() => undefined);
    return result;
  }
}
